import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import type { AdminClient, ColumnAction, RowScope } from './api';

type ChipColor = 'success' | 'warning' | 'secondary' | 'default' | 'danger' | 'primary';

/** How a column reaches an agent, as a chip. */
export const COLUMN_ACTIONS: Record<ColumnAction, { label: string; color: ChipColor }> = {
  allow: { label: 'Visible', color: 'success' },
  mask: { label: 'Masked', color: 'warning' },
  partial: { label: 'Partly masked', color: 'warning' },
  pseudonymize: { label: 'Pseudonymized', color: 'secondary' },
  omit: { label: 'Hidden', color: 'default' },
};

export const columnAction = (action: string) =>
  COLUMN_ACTIONS[action as ColumnAction] ?? { label: action, color: 'default' as const };

export const SCOPE_LABELS: Record<RowScope, string> = {
  none: 'No access',
  own: 'Own',
  group: 'Group',
  assigned: 'Assigned',
  all: 'All',
};

/** "Own + Assigned" for several scopes, "No access" for none. */
export function scopesLabel(scopes: readonly string[], t: (key: string) => string): string {
  const real = scopes.filter((scope) => scope !== 'none');
  if (real.length === 0) return t(SCOPE_LABELS.none);
  return real.map((scope) => t(SCOPE_LABELS[scope as RowScope] ?? scope)).join(' + ');
}

/** A tab's title line, in the style of AI Insight's Data Sources tab. */
export function TabIntro({ title, children, actions }: {
  readonly title: string;
  readonly children?: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="flex flex-col gap-1">
        <span className="font-semibold text-foreground">{title}</span>
        {children}
      </div>
      {actions}
    </div>
  );
}

export function ErrorLine({ message }: { readonly message: string | null | undefined }) {
  if (!message) return null;
  return (
    <p role="alert" className="flex items-center gap-2 text-sm text-danger">
      <Icon icon="material-symbols:error-outline" width={18} /> {message}
    </p>
  );
}

/**
 * Loads once when the tab first has a client (a refreshed token does not reload); `reload` asks
 * again. The request in flight is aborted when a new one starts or the tab goes away. Any client
 * works (the Enriched data tab passes its own).
 */
export function useAdminLoad<T, C = AdminClient>(client: C | null, load: (client: C, signal: AbortSignal) => Promise<T>) {
  const { t } = useTranslation();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const clientRef = useRef(client);
  clientRef.current = client;
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(async () => {
    const current = clientRef.current;
    if (!current) return;
    requestRef.current?.abort();
    const request = new AbortController();
    requestRef.current = request;
    setLoading(true);
    setError(null);
    try {
      const next = await loadRef.current(current, request.signal);
      if (!request.signal.aborted) setData(next);
    } catch (e) {
      if (!request.signal.aborted) setError((e as Error).message || t('Something went wrong.'));
    } finally {
      if (!request.signal.aborted) setLoading(false);
    }
  }, [t]);

  const ready = Boolean(client);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    if (ready) void reloadRef.current();
  }, [ready]);
  useEffect(() => () => requestRef.current?.abort(), []);

  return { data, setData, loading, error, reload };
}
